import React, { useCallback, useMemo, useState } from "react";
import { Link, useLocation, useNavigate, useParams } from "react-router-dom";
import { FiEdit2, FiTrash2, FiPlus, FiSearch, FiUserX, FiMail } from "react-icons/fi";
import Notification, { showToast } from "../../components/common/notification/Notification";
import ReviewCard from "../../components/common/formkit/ReviewCard";
import SaaSNotFoundState from "../../components/common/saas/SaaSNotFoundState";
import SaaSLoadingSkeleton from "../../components/common/saas/SaaSLoadingSkeleton";
import { getErrorText } from "../partnerNetwork/services/partnerNetworkService";
import { useListRecord, useRecordList } from "../partnerNetwork/hooks/useNetworkRecords";
import { fullName } from "../partnerNetwork/utils/people";
import PersonAvatar from "../partnerNetwork/components/PersonAvatar";
import DeleteConfirmDialog from "../partnerNetwork/components/DeleteConfirmDialog";
import { useTeamService } from "./hooks/useTeamRecords";
import { TeamStatusBadge } from "./TeamsPage";
import { TEAMS_HOME } from "./constants/teamEndpoints";
import "../../components/common/formkit/formkit.css";
import "../partnerNetwork/partnerNetwork.css";

// Search only earns its place once the list is long
const MEMBER_SEARCH_THRESHOLD = 8;

/** /partner/team/:Id - the team, then its members (list, remove, and a full-page Add Member form) */
const TeamDetailPage = () => {
  const { Id } = useParams();
  const navigate = useNavigate();
  const seed = useLocation().state?.record;
  const service = useTeamService();

  // No get-by-id endpoint: the team is read from the team list
  const loadTeams = useCallback(() => service.listTeams(), [service]);
  const { record: team, loading, error, notFound, refetch } = useListRecord(loadTeams, Id, seed, "Unable to load this team.");

  const loadMembers = useCallback(() => service.listMembers(Id), [service, Id]);
  const { items: members, loading: membersLoading, error: membersError, refetch: refetchMembers } = useRecordList(loadMembers, "Unable to load members.");

  const [memberSearch, setMemberSearch] = useState("");
  const [teamToDelete, setTeamToDelete] = useState(false);
  const [memberToRemove, setMemberToRemove] = useState(null);
  const [busy, setBusy] = useState(false);
  const closeTeamDialog = useCallback(() => setTeamToDelete(false), []);
  const closeMemberDialog = useCallback(() => setMemberToRemove(null), []);

  const visibleMembers = useMemo(() => {
    const q = memberSearch.trim().toLowerCase();
    if (!q) return members;
    return members.filter((m) => [fullName(m), m.email, m.role].some((v) => v && String(v).toLowerCase().includes(q)));
  }, [members, memberSearch]);

  const handleDeleteTeam = async () => {
    setBusy(true);
    try {
      await service.removeTeam(Id);
      showToast("Team deleted successfully", { type: "success" });
      navigate(TEAMS_HOME, { replace: true });
    } catch (err) {
      showToast(getErrorText(err, "Unable to delete team"), { type: "error" });
      setBusy(false);
      setTeamToDelete(false);
    }
  };

  const handleRemoveMember = async () => {
    setBusy(true);
    try {
      await service.removeMember(memberToRemove.id);
      showToast("Member removed successfully", { type: "success" });
      setMemberToRemove(null);
      refetchMembers();
    } catch (err) {
      showToast(getErrorText(err, "Unable to remove member"), { type: "error" });
    } finally {
      setBusy(false);
    }
  };

  if (loading) {
    return (
      <div className="fk-page" style={{ maxWidth: 1200 }} aria-busy="true" aria-label="Loading team">
        <div className="fk-skel" style={{ height: 28, width: "40%", marginBottom: 16 }} />
        <div className="fk-skel" style={{ height: 220 }} />
      </div>
    );
  }

  if (error) {
    return (
      <div className="fk-page" style={{ maxWidth: 1200 }}>
        <div className="alert alert-danger d-flex align-items-center justify-content-between" role="alert">
          <span>{error}</span>
          <div className="d-flex gap-2">
            <button type="button" className="btn btn-sm btn-outline-danger" onClick={refetch}>
              Retry
            </button>
            <Link to={TEAMS_HOME} className="btn btn-sm btn-outline-secondary">
              Back to Teams
            </Link>
          </div>
        </div>
      </div>
    );
  }

  if (notFound || !team) {
    return <SaaSNotFoundState title="Team not found" description="This team could not be found. It may have been deleted." backLink={TEAMS_HOME} backLabel="Back to Teams" />;
  }

  const addMemberPath = `${TEAMS_HOME}/${Id}/members/new`;

  return (
    <div className="fk-page" style={{ maxWidth: 1200 }}>
      <Notification />
      <div className="fk-header d-flex flex-wrap justify-content-between align-items-start gap-3">
        <div className="d-flex align-items-center gap-3">
          <PersonAvatar person={{ first_name: team.name }} size={56} />
          <div>
            <h1 className="fk-title">{team.name}</h1>
            <p className="fk-subtitle">
              <TeamStatusBadge status={team.status} />
            </p>
          </div>
        </div>
        <div className="d-flex gap-2">
          <Link to={`${TEAMS_HOME}/${Id}/edit`} state={{ record: team }} className="btn btn-outline-primary d-inline-flex align-items-center gap-2">
            <FiEdit2 size={15} /> Edit
          </Link>
          <button type="button" className="btn btn-outline-danger d-inline-flex align-items-center gap-2" onClick={() => setTeamToDelete(true)}>
            <FiTrash2 size={15} /> Delete
          </button>
        </div>
      </div>

      <div className="fk-review-list">
        <ReviewCard
          title="Team details"
          rows={[
            { label: "Total seats", value: team.total_seats },
            { label: "Status", value: team.status },
            { label: "Description", value: team.description || "No description", full: true },
          ]}
        />

        <ReviewCard title="Members">
          <div className="pn-section-head">
            <h2>{membersLoading || membersError ? "Team members" : `${members.length} ${members.length === 1 ? "member" : "members"}`}</h2>
            {members.length > 0 && (
              <Link to={addMemberPath} className="btn btn-primary btn-sm d-inline-flex align-items-center gap-2">
                <FiPlus size={15} /> Add Member
              </Link>
            )}
          </div>

          {members.length > MEMBER_SEARCH_THRESHOLD && (
            <div className="property-filter-toolbar__search mb-3" role="search">
              <FiSearch className="property-filter-toolbar__search-icon" />
              <input
                type="text"
                aria-label="Search members"
                placeholder="Search name, email or role..."
                value={memberSearch}
                onChange={(e) => setMemberSearch(e.target.value)}
                className="property-filter-toolbar__search-input"
              />
            </div>
          )}

          {membersLoading ? (
            <div aria-busy="true" aria-label="Loading members">
              <SaaSLoadingSkeleton type="list" count={3} />
            </div>
          ) : membersError ? (
            <div className="alert alert-danger d-flex align-items-center justify-content-between mb-0" role="alert">
              <span>{membersError}</span>
              <button type="button" className="btn btn-sm btn-outline-danger" onClick={refetchMembers}>
                Retry
              </button>
            </div>
          ) : members.length === 0 ? (
            <div className="text-center py-4">
              <FiUserX size={40} className="text-muted mb-2" aria-hidden="true" />
              <h3 className="h6 fw-bold">No members yet</h3>
              <p className="text-muted small mb-3">Add the people who work in this team.</p>
              <Link to={addMemberPath} className="btn btn-primary d-inline-flex align-items-center gap-2">
                <FiPlus size={16} /> Add Member
              </Link>
            </div>
          ) : visibleMembers.length === 0 ? (
            <p className="text-muted mb-0">No members match "{memberSearch.trim()}".</p>
          ) : (
            <ul className="pn-rows">
              {visibleMembers.map((member) => (
                <li key={member.id} className="pn-row">
                  <PersonAvatar person={member} />
                  <div className="pn-row__main">
                    <span className="pn-row__name">{fullName(member)}</span>
                    {member.email && (
                      <span className="pn-details">
                        <span className="pn-details__email" title={member.email}>
                          <FiMail aria-hidden="true" /> {member.email}
                        </span>
                      </span>
                    )}
                  </div>
                  {member.role && <span className="pn-badge">{member.role}</span>}
                  <div className="pn-actions">
                    <button type="button" className="btn btn-sm btn-outline-danger" title="Remove" aria-label={`Remove ${fullName(member)}`} onClick={() => setMemberToRemove(member)}>
                      <FiTrash2 size={14} />
                    </button>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </ReviewCard>
      </div>

      {teamToDelete && (
        <DeleteConfirmDialog title="Delete team?" message={`"${team.name}" will be removed from your teams.`} busy={busy} onCancel={closeTeamDialog} onConfirm={handleDeleteTeam} />
      )}
      {memberToRemove && (
        <DeleteConfirmDialog
          title="Remove member?"
          message={`"${fullName(memberToRemove)}" will be removed from this team.`}
          confirmLabel="Remove"
          busy={busy}
          onCancel={closeMemberDialog}
          onConfirm={handleRemoveMember}
        />
      )}
    </div>
  );
};

export default TeamDetailPage;
